// src/app/NotFound.tsx: any path the router does not know (spec 2.3), and the unknown-host screen.
import { tenant } from "../state/session";
import { Button } from "../ui/Button";
import { Logo } from "../ui/Logo";
import type { PublicTenant } from "../../shared/tenant";
import { Door } from "../start/Door";
import { siteHost, siteHref } from "../lib/site-link";

/** A plain page with the builder's logo on top (left-aligned), for screens outside the shells. */
export function PlainScreen(props: { children?: preact.ComponentChildren }) {
  return (
    <div class="hb-screen">
      {tenant.value ? (
        <header class="hb-screen-top">
          <div class="wrap">
            <Logo />
          </div>
        </header>
      ) : null}
      <main class="hb-screen-main" id="main" tabIndex={-1}>
        {props.children}
      </main>
    </div>
  );
}

export function NotFound() {
  return (
    <PlainScreen>
      <div class="hb-not-found">
        <h1 class="h-page">Page Not Found</h1>
        <p class="t-lede">We couldn't find that page.</p>
        <div class="hb-actions">
          <Button href="/" iconAfter="arrow-r">
            Go to Your Board
          </Button>
        </div>
      </div>
    </PlainScreen>
  );
}

/** Shown when the host belongs to no tenant (GET /api/tenant answered 404). */
export function UnknownHost() {
  return (
    <main class="hb-unknown-host">
      <h1>This page isn't available</h1>
    </main>
  );
}

/**
 * A builder in preview (setup) shows the public its own page (the Start Your Build frame, a photo of one of its homes, one plain line;
 * docs/DECISIONS.md "Free preview, Activate to open, 30-day money-back guarantee"): its address and its Start Your Build take no family
 * yet. The phone only when there is one. The team's own pages and sign-in still open (app/App.tsx), and a signed-in team member can try
 * Start Your Build as a test family.
 */
export function OpeningSoonScreen() {
  const t = tenant.value;
  if (!t) return <UnknownHost />;
  return (
    <ClosedDoor t={t} screen="OpeningSoon" title="Opening soon">
      {t.name} isn't taking new families online yet.
    </ClosedDoor>
  );
}

/**
 * A paused builder's public address (site status "paused"): the same frame as Opening soon, with its own words. The team's pages and
 * sign-in still open (app/App.tsx).
 */
export function PausedScreen() {
  const t = tenant.value;
  if (!t) return <UnknownHost />;
  return (
    <ClosedDoor t={t} screen="Paused" title="Not taking new families">
      {t.name} isn't taking new families online right now.
    </ClosedDoor>
  );
}

/** The builder's own page for a family who finds the address before it is open: a home of theirs when they have a photo, one line, the phone. */
function ClosedDoor(props: { t: PublicTenant; screen: string; title: string; children: preact.ComponentChildren }) {
  const { t } = props;
  const phone = t.contact.phone?.trim() ?? "";
  const home = t.homes.find((h) => h.hero);
  return (
    <Door screen={props.screen} right="none">
      <div class="cl">
        {home?.hero ? <img class="cl-photo" src={home.heroSmall ?? home.hero} alt="" /> : null}
        <h1 class="cl-h1">{props.title}</h1>
        <p class="cl-line">{props.children}</p>
        {phone ? (
          <Button href={t.contact.phoneHref} size="lg" icon="phone">
            Call {phone}
          </Button>
        ) : null}
        <a class="quiet-link" href={siteHref(t)}>
          Back to {siteHost(t.contact.site)}
        </a>
      </div>
    </Door>
  );
}
